import { create } from "zustand";
import debounce from "lodash.debounce";
import {
  fetchRecommendations as apiFetchRecommendations,
  upsertRecommendation as apiUpsertRecommendation,
  deleteRecommendation as apiDeleteRecommendation,
  upsertComment as apiUpsertComment,
  upsertWholesaleMarkups as apiUpsertWholesaleMarkups,
} from "../services/recommendationService";
import { fetchCategories as apiFetchCategories } from "../services/categoriesService";
import {
  fetchCustomTags as apiFetchCustomTags,
  assignCustomTag as apiAssignCustomTag,
  removeCustomTag as apiRemoveCustomTag,
} from "../services/customTagsService";
import fieldMappers from "../utils/fieldMappers";
import { buildCategoryTree, flattenCategoryTree } from "utils";

import { useErrorStore } from "components";

// Глобальний стор (state manager) сторінки рекомендацій
export const useRecommendationStore = create((set, get) => ({
  stocks: [],
  totalCount: 0,
  isLoading: true,
  categoryOptions: [],
  customTags: [],

  filters: {
    warehouse_id: 0,
    search: "",
    category_id: null,
    is_full: false,
    has_custom_price: false,
    in_transit: false,
    tag_ids: [],
    page: 1,
    page_size: 100,
    sort_by: "name",
    sort_asc: false,
  },

  viewSettings: {
    priceViewMode: "price_05",
  },

  // Debounced-функція для відкладеного оновлення даних з сервера
  debouncedRefresh: debounce(() => {
    get().fetchRecommendations();
  }, 20000),

  // Ініціалізує стор при першому завантаженні сторінки
  init: async () => {
    get().initFiltersFromURL();
    const catPromise = get().fetchCategories();
    const tagsPromise = get().fetchCustomTags();
    await get().fetchRecommendations();
    await Promise.all([catPromise, tagsPromise]);
  },

  // Ініціалізує стан фільтрів з параметрів URL.
  initFiltersFromURL: () => {
    const params = new URLSearchParams(window.location.search);
    set({
      filters: {
        ...get().filters, // Зберігаємо дефолтні значення
        warehouse_id: parseInt(params.get("warehouse_id") || "0", 10),
        search: params.get("search") || "",
        category_id: parseInt(params.get("category_id"), 10) || null,
        is_full: params.get("is_full") === "1",
        has_custom_price: params.get("has_custom_price") === "1",
        in_transit: params.get("in_transit") === "1",
        tag_ids: (params.get("tag_ids") || "")
          .split(",")
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id)),
        page: parseInt(params.get("page") || "1", 10),
        sort_by: params.get("sort_by") || "name",
        sort_asc: params.get("sort_asc") === "1",
      },
      viewSettings: {
        ...get().viewSettings,
        priceViewMode: params.get("priceViewMode") || "price_05",
      },
    });
  },

  // Синхронізує поточний стан фільтрів з URL.
  syncFiltersToURL: () => {
    const { filters, viewSettings } = get();
    const params = new URLSearchParams();

    if (filters.warehouse_id !== 0) params.set("warehouse_id", filters.warehouse_id);
    if (filters.search) params.set("search", filters.search);
    if (filters.category_id) params.set("category_id", filters.category_id);
    if (filters.is_full) params.set("is_full", "1");
    if (filters.has_custom_price) params.set("has_custom_price", "1");
    if (filters.in_transit) params.set("in_transit", "1");
    if (filters.tag_ids.length > 0) params.set("tag_ids", filters.tag_ids.join(","));
    if (filters.page !== 1) params.set("page", filters.page);
    if (filters.sort_by !== "name") params.set("sort_by", filters.sort_by);
    if (filters.sort_asc) params.set("sort_asc", "1");
    if (viewSettings.priceViewMode !== "price_05") {
      params.set("priceViewMode", viewSettings.priceViewMode);
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
  },

  // Керує логікою фільтрації таблиці
  applyFilters: (newFilters) => {
    set((state) => ({
      filters: {
        ...state.filters,
        ...newFilters,
        page: 1,
      },
    }));
    get().syncFiltersToURL();
    get().fetchRecommendations();
  },

  // Керує логікою сортування таблиці
  setSort: (field) => {
    const { sort_by, sort_asc } = get().filters;
    if (sort_by === field) {
      set((state) => ({
        filters: { ...state.filters, sort_asc: !sort_asc },
      }));
    } else {
      set((state) => ({
        filters: { ...state.filters, sort_by: field, sort_asc: false, page: 1 },
      }));
    }
    get().syncFiltersToURL();
    get().fetchRecommendations();
  },

  // Керує логікою пагінації
  setPage: (page) => {
    set((state) => ({
      filters: { ...state.filters, page },
    }));
    get().syncFiltersToURL();
    get().fetchRecommendations();
  },

  // Керує логікою пошуку
  setSearch: (value) => {
    set((state) => ({
      filters: { ...state.filters, search: value, page: 1 },
    }));
    get().syncFiltersToURL();
    get().fetchRecommendations();
  },

  // Керує логікою вибору складу
  setWarehouse: (id) => {
    set((state) => ({
      filters: {
        ...state.filters,
        warehouse_id: id,
        page: 1,
        // Фільтр кастомних значень у кожного розділу свій — скидаємо при переході
        has_custom_price: false,
      },
    }));
    get().syncFiltersToURL();
    get().fetchRecommendations();
  },

  // Керує логікою вибору категорії
  setIsFull: (value) => {
    set((state) => ({
      filters: { ...state.filters, is_full: value, page: 1 },
    }));
    get().syncFiltersToURL();
    get().fetchRecommendations();
  },

  // Керує логікою вибору кастомної ціни
  setHasCustomPrice: (value) => {
    set((state) => ({
      filters: { ...state.filters, has_custom_price: value, page: 1 },
    }));
    get().syncFiltersToURL();
    get().fetchRecommendations();
  },

  // Керує фільтром «Очікуваний прихід»: лише товари, які їдуть на склад
  setInTransit: (value) => {
    set((state) => ({
      filters: { ...state.filters, in_transit: value, page: 1 },
    }));
    get().syncFiltersToURL();
    get().fetchRecommendations();
  },

  // Керує логікою фільтрації за кастомною міткою
  toggleTagFilter: (tagId) => {
    set((state) => {
      const isActive = state.filters.tag_ids.includes(tagId);
      return {
        filters: {
          ...state.filters,
          tag_ids: isActive
            ? state.filters.tag_ids.filter((id) => id !== tagId)
            : [...state.filters.tag_ids, tagId],
          page: 1,
        },
      };
    });
    get().syncFiltersToURL();
    get().fetchRecommendations();
  },

  // Керує логікою переключення режиму відображення цін
  setPriceViewMode: (mode) => {
    set((state) => ({
      viewSettings: {
        ...state.viewSettings,
        priceViewMode: mode,
      },
    }));
    get().syncFiltersToURL();
  },

  // Завантажує категорії один раз.
  fetchCategories: async () => {
    try {
      const data = await apiFetchCategories();
      const tree = buildCategoryTree(data);
      const options = flattenCategoryTree(tree);
      set({ categoryOptions: options });
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  },

  // Завантажує список доступних кастомних міток один раз.
  fetchCustomTags: async () => {
    try {
      const { data } = await apiFetchCustomTags();
      set({ customTags: data });
    } catch (err) {
      console.error("Failed to fetch custom tags:", err);
    }
  },

  // Основна функція для завантаження рекомендованих товарів.
  fetchRecommendations: async () => {
    get().debouncedRefresh.cancel();

    set({ isLoading: true });
    const { filters } = get();
    try {
      const { data, totalCount: total } = await apiFetchRecommendations({
        warehouse_id: filters.warehouse_id,
        search: filters.search,
        category_id: filters.category_id,
        is_full: filters.is_full,
        has_custom_price: filters.has_custom_price,
        in_transit: filters.in_transit,
        tag_ids: filters.tag_ids,
        page: filters.page,
        page_size: filters.page_size,
        sort_by: filters.sort_by,
        sort_asc: filters.sort_asc,
      });
      set({ stocks: data, totalCount: total });
    } catch (error) {
      console.error("Failed to fetch recommendations:", error);
      set({ stocks: [], totalCount: 0 }); // Скидаємо дані в разі помилки
    } finally {
      set({ isLoading: false });
    }
  },

  // Функція для оновлення рекомендованих цін
  optimisticUpdateRecommendation: async (warehouse_id, article, fields) => {
    // Оновлюємо бекенд-дані в фоновому режимі
    apiUpsertRecommendation(warehouse_id, article, fields).catch((err) => {
      console.error("Помилка при фоновому оновленні цін:", err);

      // Показуємо вікно з помилкою
      const { showError } = useErrorStore.getState();
      showError("Не вдалося зберегти ціну. Зміни скасовано.");

      get().fetchRecommendations(); // Примусове оновлення в разі помилки
    });

    // Миттєво оновлюємо фронтенд-дані
    set((state) => ({
      stocks: state.stocks.map((item) => {
        if (item.article === article) {
          const updatedItem = { ...item };
          Object.keys(fields).forEach((key) => {
            const frontendKey = fieldMappers(key, "toFrontend");
            if (frontendKey) {
              updatedItem[frontendKey] = fields[key];
            }
          });
          updatedItem.is_reco = true;
          return updatedItem;
        }
        return item;
      }),
    }));

    // Запускаємо/перезапускаємо відкладений таймер
    get().debouncedRefresh();
  },

  // Функція для оновлення коментаря
  optimisticUpdateComment: async (warehouse_id, article, comment) => {
    // Оновлюємо бекенд-дані в фоновому режимі
    apiUpsertComment(warehouse_id, article, comment).catch((err) => {
      console.error("Помилка при фоновому оновленні коментаря:", err);

      // Показуємо вікно з помилкою
      const { showError } = useErrorStore.getState();
      showError("Не вдалося зберегти коментар. Зміни скасовано.");

      get().fetchRecommendations();
    });

    // Миттєво оновлюємо фронтенд-дані
    set((state) => ({
      stocks: state.stocks.map((item) =>
        item.article === article ? { ...item, comment: comment } : item,
      ),
    }));

    // Запускаємо/перезапускаємо відкладений таймер
    get().debouncedRefresh();
  },

  // Функція для збереження індивідуальних оптових націнок товару
  updateWholesaleMarkups: async (article, markups) => {
    try {
      await apiUpsertWholesaleMarkups(article, markups);
    } catch (err) {
      console.error("Помилка при збереженні оптових націнок:", err);

      // Показуємо вікно з помилкою
      const { showError } = useErrorStore.getState();
      showError("Не вдалося зберегти оптові націнки.");
      return;
    }

    // Якщо всі рівні стандартні — індивідуальних націнок у товару немає
    const hasCustom = Object.values(markups).some((value) => value !== null);
    set((state) => ({
      stocks: state.stocks.map((item) =>
        item.article === article
          ? { ...item, wholesale_markups: hasCustom ? markups : null }
          : item,
      ),
    }));
  },

  // Локально оновлює список міток конкретного товару
  setArticleTags: (article, updater) => {
    set((state) => ({
      stocks: state.stocks.map((item) =>
        item.article === article ? { ...item, tags: updater(item.tags || []) } : item,
      ),
    }));
  },

  // Функція для додавання мітки до товару
  assignCustomTag: async (article, tagId) => {
    const tag = get().customTags.find((t) => t.id === tagId);
    if (!tag) return;

    // Миттєво оновлюємо фронтенд-дані, зберігаючи порядок міток як на бекенді
    const tagOrder = get().customTags.map((t) => t.id);
    get().setArticleTags(article, (tags) =>
      [...tags.filter((t) => t.id !== tagId), tag].sort(
        (a, b) => tagOrder.indexOf(a.id) - tagOrder.indexOf(b.id),
      ),
    );

    try {
      await apiAssignCustomTag(tagId, article);
    } catch (err) {
      console.error("Помилка при додаванні мітки:", err);

      // Показуємо вікно з помилкою та відкочуємо зміни
      const { showError } = useErrorStore.getState();
      showError("Не вдалося додати мітку. Зміни скасовано.");

      get().setArticleTags(article, (tags) => tags.filter((t) => t.id !== tagId));
    }
  },

  // Функція для видалення мітки з товару
  removeCustomTag: async (article, tagId) => {
    const previousTags =
      get().stocks.find((item) => item.article === article)?.tags || [];

    // Миттєво оновлюємо фронтенд-дані
    get().setArticleTags(article, (tags) => tags.filter((t) => t.id !== tagId));

    try {
      await apiRemoveCustomTag(tagId, article);
    } catch (err) {
      console.error("Помилка при видаленні мітки:", err);

      // Показуємо вікно з помилкою та відкочуємо зміни
      const { showError } = useErrorStore.getState();
      showError("Не вдалося видалити мітку. Зміни скасовано.");

      get().setArticleTags(article, () => previousTags);
    }
  },

  // Функція для додавання рекомендації
  upsertRecommendation: async (warehouse_id, article, fields) => {
    await apiUpsertRecommendation(warehouse_id, article, fields);
    get().fetchRecommendations();
  },

  // Функція для видалення рекомендації
  deleteRecommendation: async (warehouse_id, article) => {
    await apiDeleteRecommendation(warehouse_id, article);
    get().fetchRecommendations();
  },
}));
